export const MONEY_LOCALE = 'sr-RS';
export const DEFAULT_CURRENCY = 'RSD';

const formatter = new Intl.NumberFormat(MONEY_LOCALE, {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * Amounts cross the wire as plain numbers with two decimals, matching the backend's
 * numeric(12,2). Everything here is display only - the backend is the authority on
 * rounding, so never sum these values and send the result back as a total.
 */
export const formatMoney = (amount?: number | null, options?: { withCurrency?: boolean; currency?: string }): string => {
  if (amount === undefined || amount === null || Number.isNaN(amount)) {
    return '-';
  }
  const text = formatter.format(amount);
  return options?.withCurrency ? `${text} ${options.currency ?? DEFAULT_CURRENCY}` : text;
};

/** One to three digits, then groups of exactly three: '24.500.000', never '0.125' or '1.2.3'. */
const isGrouped = (integer: string, separator: '.' | ',') => new RegExp(`^-?[1-9]\\d{0,2}(\\${separator}\\d{3})+$`).test(integer);

/**
 * Parses what a person actually types: '24.500,00', '24500,5' or '24500.5'.
 *
 * Serbian writes 24.500,00, but people type on whatever keyboard they have, so both
 * separators have to be read for what they mean rather than assumed. The rule:
 *
 *  - both present: the rightmost one is the decimal point and appears once; the other
 *    groups thousands, and only counts as that when the groups are well formed
 *    ('1.234,56' yes, '1.2.3,45' no)
 *  - a lone comma is always the decimal point, because that is what it is here; three
 *    digits after it is too many decimals, which is a typo rather than thousands, and a
 *    second comma is a typo too
 *  - a lone dot is ambiguous: when the dots group thousands properly ('24.500',
 *    '24.500.000') they do; otherwise a single dot is the decimal point ('9000.50'), and
 *    anything else ('1.2.3', '12.50.00') is a typo. '0.500' and '1234.567' are therefore
 *    not thousands - no amount groups as 0.500, or with four digits before a dot - but a
 *    decimal point with three decimals, which is a typo like '12,345'
 *
 * Returns null for empty input and undefined when the text is not a valid amount, so
 * a caller can tell "cleared" apart from "typo".
 */
export const parseMoneyInput = (raw: string): number | null | undefined => {
  const trimmed = raw.replace(/\s/g, '');
  if (!trimmed) return null;
  if (!/^-?[\d.,]+$/.test(trimmed)) return undefined;

  const lastDot = trimmed.lastIndexOf('.');
  const lastComma = trimmed.lastIndexOf(',');
  const count = (separator: string) => trimmed.split(separator).length - 1;
  let decimalAt = -1;

  if (lastDot >= 0 && lastComma >= 0) {
    decimalAt = Math.max(lastDot, lastComma);
    const decimal = trimmed[decimalAt];
    // a decimal point appears once; more than one means the text is malformed rather
    // than merely written in the other convention
    if (count(decimal) > 1) return undefined;
    if (!isGrouped(trimmed.slice(0, decimalAt), decimal === ',' ? '.' : ',')) return undefined;
  } else if (lastComma >= 0) {
    if (count(',') > 1) return undefined;
    decimalAt = lastComma;
  } else if (lastDot >= 0 && !isGrouped(trimmed, '.')) {
    if (count('.') > 1) return undefined;
    decimalAt = lastDot;
  }

  const digitsOnly = (value: string) => value.replace(/[.,]/g, '');
  const normalised = decimalAt >= 0 ? `${digitsOnly(trimmed.slice(0, decimalAt))}.${trimmed.slice(decimalAt + 1)}` : digitsOnly(trimmed);

  if (!/^-?\d+(\.\d{1,2})?$/.test(normalised)) return undefined;
  // via minor units so 0.1 + 0.2 style error never enters the value
  return Math.round(Number(normalised) * 100) / 100;
};

/** What goes in the input while it has focus: no thousands separator, easier to edit. */
export const toMoneyInputValue = (amount?: number | null): string =>
  amount === undefined || amount === null ? '' : amount.toFixed(2).replace('.', ',');

export const formatPercent = (value?: number | null): string => (value === undefined || value === null ? '-' : `${value}%`);

export const formatQuantity = (value?: number | null): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat(MONEY_LOCALE, { maximumFractionDigits: 3 }).format(value);
};

/* ------------------------------------------------------------------ *
 * Live totals while editing
 *
 * Mirrors the backend rounding exactly: round once per line in minor units, then
 * sum the already-rounded values. Any other order (rounding the total, or summing
 * unrounded lines) makes the preview disagree with what comes back after saving.
 *
 * The server stays the authority - this only removes the blind spot between typing
 * a price and saving the draft.
 * ------------------------------------------------------------------ */

const toMinor = (amount: number): number => Math.round(amount * 100);
const fromMinor = (minor: number): number => minor / 100;

export interface LineTotals {
  net: number;
  vat: number;
  gross: number;
}

export const calculateLineTotals = (unitPriceNet: number, quantity: number, vatRate = 0, discountPercent = 0): LineTotals => {
  const gross = toMinor(unitPriceNet || 0) * (quantity || 0);
  const net = Math.round(gross - (gross * (discountPercent || 0)) / 100);
  const vat = Math.round((net * (vatRate || 0)) / 100);
  return { net: fromMinor(net), vat: fromMinor(vat), gross: fromMinor(net + vat) };
};

export const sumLineTotals = (totals: LineTotals[]): LineTotals => {
  let net = 0;
  let vat = 0;
  let gross = 0;
  for (const total of totals) {
    net += toMinor(total.net);
    vat += toMinor(total.vat);
    gross += toMinor(total.gross);
  }
  return { net: fromMinor(net), vat: fromMinor(vat), gross: fromMinor(gross) };
};
